<template>
  <div
    class="boot-intro"
    :class="{ compact: menusCompact, reduced: reducedMotion }"
    role="dialog"
    aria-modal="true"
    :aria-label="t('nav.brand')"
  >
    <div class="void" aria-hidden="true" />
    <div class="vignette" aria-hidden="true" />
    <div class="sparks" aria-hidden="true">
      <span
        v-for="spark in sparks"
        :key="spark.i"
        :style="spark.style"
      />
    </div>

    <div class="storm" aria-hidden="true">
      <div
        v-for="card in flyers"
        :key="card.key"
        class="flyer"
        :style="card.style"
      >
        <div
          class="flip-inner"
          :class="{ live: card.ready && !card.flip, flipping: card.ready && card.flip }"
        >
          <img
            class="face back"
            :src="CARD_BACK_URL"
            alt=""
          >
          <img
            class="face front"
            :src="card.src"
            alt=""
            @load="onCardLoad(card.key)"
          >
        </div>
      </div>
    </div>

    <div class="shock" aria-hidden="true" />

    <div class="brand-slam">
      <img
        class="mark"
        src="/icons/logo-mark.jpg"
        :alt="t('nav.brand')"
      >
      <p class="name">{{ t('nav.brand') }}</p>
    </div>

    <button
      type="button"
      class="skip"
      @click="finish"
    >
      {{ t('splash.skip') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { allCatalogCards } from '../game/packs'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { CARD_BACK_URL, getLocalCardImageUrl } from '../utils/cardImages'

const emit = defineEmits<{ done: [] }>()
const { t } = useI18n()
const { menusCompact } = useCompactLayout()

const ANCHORS = [
  'Blue-Eyes White Dragon',
  'Dark Magician',
  'Exodia the Forbidden One',
]

const AUTO_MS = 5600
const reducedMotion = ref(false)
let timer = 0

type Flyer = {
  key: string
  src: string
  flip: boolean
  ready: boolean
  style: Record<string, string>
}

function shuffled<T>(items: readonly T[]): T[] {
  const out = items.slice()
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

function resolveAnchors(): string[] {
  const byName = new Map(allCatalogCards().map((card) => [card.name, card.id]))
  return ANCHORS
    .map((name) => byName.get(name))
    .filter((id): id is string => Boolean(id))
}

/** Las tres icónicas siempre entran; el resto sale del catálogo completo. */
function pickIds(count: number, anchors: string[]): string[] {
  const cards = allCatalogCards()
  const pool = shuffled(cards.filter((card) => !anchors.includes(card.id)))
    .map((card) => card.id)
  const fill = pool.slice(0, Math.max(0, count - anchors.length))
  return shuffled([...anchors, ...fill])
}

function jitter(range: number): number {
  return (Math.random() * 2 - 1) * range
}

function buildFlyers(): Flyer[] {
  const count = menusCompact.value ? 16 : 28
  const anchors = resolveAnchors()
  const featuredIds = new Set(anchors)
  const ids = pickIds(count, anchors)
  const spin = Math.random() * Math.PI * 2
  return ids.map((id, i) => {
    const n = ids.length
    const featured = featuredIds.has(id)
    const ang = (i / n) * Math.PI * 2 + spin
    const ringBase = menusCompact.value ? 38 : 32
    const ring = ringBase + jitter(4)
    const start = ang + Math.PI * (0.55 + jitter(0.22))
    const x0 = Math.cos(start) * (88 + jitter(8))
    const y0 = Math.sin(start) * (88 + jitter(6))
    const x1 = Math.cos(ang) * ring
    const y1 = Math.sin(ang) * ring
    const w = (featured ? 92 : 72) + jitter(6)
    const delay = (i * 0.045).toFixed(3)
    const dur = (1.55 + Math.random() * 0.6).toFixed(2)
    const r0 = jitter(80)
    const r1 = jitter(25)
    return {
      key: `${id}-${i}`,
      src: getLocalCardImageUrl(id),
      flip: Math.random() < 0.7,
      ready: false,
      style: {
        '--w': `${w.toFixed(0)}px`,
        '--x0': `${x0.toFixed(1)}vmin`,
        '--y0': `${y0.toFixed(1)}vmin`,
        '--x1': `${x1.toFixed(1)}vmin`,
        '--y1': `${y1.toFixed(1)}vmin`,
        '--r0': `${r0.toFixed(0)}deg`,
        '--r1': `${r1.toFixed(0)}deg`,
        '--delay': `${delay}s`,
        '--dur': `${dur}s`,
        '--z': String(featured ? 24 : 10 + Math.floor(Math.random() * 8)),
        '--flip-delay': `${(0.55 + i * 0.04).toFixed(2)}s`,
      },
    }
  })
}

const flyers = ref<Flyer[]>([])
const sparks = Array.from({ length: 36 }, (_, i) => ({
  i,
  style: {
    left: `${4 + ((i * 17) % 92)}%`,
    top: `${6 + ((i * 29) % 84)}%`,
    '--delay': `${(i * 0.11).toFixed(2)}s`,
    '--dur': `${(2.4 + (i % 5) * 0.4).toFixed(2)}s`,
  },
}))

function onCardLoad(key: string): void {
  const card = flyers.value.find((item) => item.key === key)
  if (card) card.ready = true
}

function finish(): void {
  if (timer) window.clearTimeout(timer)
  emit('done')
}

onMounted(() => {
  reducedMotion.value = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches
  if (reducedMotion.value) {
    timer = window.setTimeout(finish, 700)
    return
  }
  flyers.value = buildFlyers()
  timer = window.setTimeout(finish, AUTO_MS)
})

onUnmounted(() => {
  if (timer) window.clearTimeout(timer)
})
</script>

<style scoped>
.boot-intro {
  position: fixed;
  inset: 0;
  z-index: 120;
  overflow: hidden;
  background: #07070a;
  color: #f5f5f5;
  perspective: 1400px;
}

.void {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(circle at 50% 48%, rgba(232, 197, 71, 0.28), transparent 42%),
    radial-gradient(circle at 50% 50%, #14151c 0%, #07070a 62%);
  animation: pulse 2.8s ease-in-out infinite;
}

.vignette {
  position: absolute;
  inset: 0;
  box-shadow: inset 0 0 120px 40px #07070a;
  pointer-events: none;
}

.sparks span {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #f4e38a;
  box-shadow: 0 0 8px 2px rgba(232, 197, 71, 0.8);
  animation: spark var(--dur) var(--delay) ease-in-out infinite;
}

.storm {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  animation: orbit 48s 2.5s linear infinite;
}

.flyer {
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--w);
  aspect-ratio: 421 / 614;
  margin: 0;
  z-index: var(--z);
  pointer-events: none;
  filter: drop-shadow(0 12px 18px rgba(0, 0, 0, 0.65));
  animation: fly var(--dur) var(--delay) cubic-bezier(0.18, 0.7, 0.2, 1) both;
}

.flip-inner {
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
}

.flip-inner.live {
  transform: rotateY(180deg);
}

.flip-inner.flipping {
  animation: flipY 0.7s var(--flip-delay) cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 7px;
  border: 1px solid rgba(232, 197, 71, 0.4);
  backface-visibility: hidden;
}

.front {
  transform: rotateY(180deg);
}

.shock {
  position: absolute;
  left: 50%;
  top: 48%;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(232, 197, 71, 0.85);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: shock 1.1s 1.05s ease-out both;
  pointer-events: none;
}

.brand-slam {
  position: absolute;
  left: 50%;
  top: 48%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: min(420px, 86vw);
  transform: translate(-50%, -50%);
  animation: slam 0.9s 1.05s cubic-bezier(0.16, 0.9, 0.2, 1) both;
  text-align: center;
  z-index: 20;
  pointer-events: none;
}

.mark {
  width: min(220px, 46vw);
  height: auto;
  border-radius: 28%;
  box-shadow:
    0 0 0 1px rgba(232, 197, 71, 0.35),
    0 18px 50px rgba(0, 0, 0, 0.55),
    0 0 40px rgba(232, 197, 71, 0.35);
}

.name {
  margin: 0;
  font-family: Cinzel, serif;
  font-size: clamp(22px, 4.2vw, 40px);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #e8c547;
  text-shadow: 0 2px 18px rgba(232, 197, 71, 0.45);
}

.skip {
  position: absolute;
  top: max(12px, env(safe-area-inset-top));
  right: max(12px, env(safe-area-inset-right));
  z-index: 30;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid rgba(232, 197, 71, 0.45);
  border-radius: 999px;
  background: rgba(19, 19, 22, 0.72);
  color: #e8c547;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.skip:hover {
  background: rgba(232, 197, 71, 0.16);
}

.compact .mark {
  width: min(160px, 42vw);
}

.compact .name {
  font-size: 20px;
}

.compact .flyer {
  width: calc(var(--w) * 0.78);
}

.reduced .storm,
.reduced .sparks,
.reduced .shock {
  display: none;
}

.reduced .brand-slam {
  animation: none;
  opacity: 1;
}

@keyframes fly {
  0% {
    transform: translate(-50%, -50%) translate3d(var(--x0), var(--y0), -240px)
      rotate(var(--r0)) scale(0.55);
    opacity: 0;
  }
  14% {
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) translate3d(var(--x1), var(--y1), 40px)
      rotate(var(--r1)) scale(1);
    opacity: 1;
  }
}

@keyframes flipY {
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(180deg);
  }
}

@keyframes slam {
  0% {
    opacity: 0;
    filter: blur(16px);
    transform: translate(-50%, -50%) scale(1.85);
  }
  62% {
    opacity: 1;
    filter: blur(0);
    transform: translate(-50%, -50%) scale(0.94);
  }
  80% {
    transform: translate(-50%, -50%) scale(1.04);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes shock {
  0% {
    opacity: 0.9;
    width: 20px;
    height: 20px;
  }
  100% {
    opacity: 0;
    width: min(90vw, 720px);
    height: min(90vw, 720px);
  }
}

@keyframes spark {
  0%,
  100% {
    opacity: 0.15;
    transform: scale(0.6);
  }
  50% {
    opacity: 1;
    transform: scale(1.4);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.82;
  }
}

@keyframes orbit {
  to {
    transform: rotate(360deg);
  }
}

@media (max-height: 500px) {
  .brand-slam {
    top: 46%;
    gap: 8px;
  }

  .mark {
    width: min(120px, 28vw);
  }

  .name {
    font-size: 18px;
  }
}
</style>
